import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Divider, Grid, Paper, Typography, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Tooltip, CircularProgress, Menu, MenuItem,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import FlightIcon from "@mui/icons-material/Flight";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getUsersByCompany } from "services/firebaseServices";
import { getPoliciesByCompany } from "services";
import { getProfile } from "utilities";
import { collection, getDocs, query, where } from "firebase/firestore";
import { fireStoreDB } from "services";
import QuoteWizard from "./QuoteWizard";
import { CancelDialog, ModifyDialog } from "./PolicyActions";

const STATUS_STYLES = {
  approved: { bg: "#d1fae5", color: "#065f46", label: "Active" },
  pending: { bg: "#fef3c7", color: "#92400e", label: "Pending payment" },
  cancelled: { bg: "#fee2e2", color: "#991b1b", label: "Cancelled" },
};

const POLICY_URL = process.env.REACT_APP_INSURANCE_POLICY_URL || "";
const DISCLOSURES_URL =
  process.env.REACT_APP_INSURANCE_DISCLOSURES_URL ||
  "https://www.battleface.com/documents/cf-spc-disclosures-for-non-admitted-travel-and-medical-plans.pdf";
const LEGAL_FOOTER =
  "Travel medical insurance plan administered by battleface Insurance Services Ltd. Insurance benefits are underwritten by Crum & Forster, SPC. C&F and Crum & Forster are registered trademarks of Crum & Forster, SPC. The Crum & Forster group of companies is rated A+ (Superior) by AM Best Company 2025.";
const CONDITIONS_NOTE =
  "Annual multi-trip cover, up to 30 days per trip. Excludes domestic trips. Pre-existing medical conditions excluded. Cover limits, deductible and exclusions are set out in full in the policy wording.";

const coverageItems = [
  { icon: <FlightIcon />, color: "#2563eb", bg: "#dbeafe", title: "Travel & emergency medical", deductible: "€100 deductible", description: "Up to €500,000 for emergency medical treatment and hospitalisation abroad for travelling employees." },
  { icon: <LocalHospitalIcon />, color: "#10b981", bg: "#d1fae5", title: "Emergency evacuation & repatriation", deductible: "", description: "Up to €100,000 for evacuation and repatriation, including medical escort and transport home." },
  { icon: <ShieldIcon />, color: "#8b5cf6", bg: "#ede9fe", title: "Personal liability", deductible: "€100 deductible", description: "Up to €500,000 against third-party claims arising from business travel." },
  { icon: <FlightIcon />, color: "#0891b2", bg: "#cffafe", title: "Luggage & personal effects", deductible: "€100 deductible", description: "Up to €2,000 for lost or damaged baggage, plus delay cover." },
  { icon: <CheckCircleIcon />, color: "#f59e0b", bg: "#fef3c7", title: "24/7 assistance", deductible: "", description: "Round-the-clock emergency assistance for employees wherever they travel." },
];

export default function Insurance() {
  const [employees, setEmployees] = useState([]);
  const [company, setCompany] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cancelPolicy, setCancelPolicy] = useState(null);
  const [modifyPolicy, setModifyPolicy] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPolicy, setMenuPolicy] = useState(null);

  const loadPolicies = async (companyName) => {
    const list = await getPoliciesByCompany(companyName);
    setPolicies(list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
  };

  const load = async () => {
    setLoading(true);
    try {
      const profile = getProfile();
      if (!profile?.uid) return;
      const q = query(collection(fireStoreDB, "businesses"), where("userId", "==", profile.uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const biz = snap.docs[0].data();
        setCompany(biz.companyName);
        const [emp] = await Promise.all([getUsersByCompany(biz.companyName), loadPolicies(biz.companyName)]);
        setEmployees(emp || []);
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activePolicies = useMemo(() => policies.filter((p) => p.status === "approved"), [policies]);
  const insuredCount = useMemo(() => {
    const names = new Set();
    activePolicies.forEach((p) => (p.travellers || []).forEach((t) => names.add(t.email || `${t.firstName} ${t.lastName}`)));
    return names.size;
  }, [activePolicies]);
  const totalEmployees = employees.length;
  const coverageRate = totalEmployees > 0 ? Math.round((insuredCount / totalEmployees) * 100) : 0;

  const openMenu = (e, policy) => { setMenuAnchor(e.currentTarget); setMenuPolicy(policy); };
  const closeMenu = () => { setMenuAnchor(null); setMenuPolicy(null); };

  const stats = [
    { label: "Total Employees", value: loading ? "…" : totalEmployees, color: "#2563eb", bg: "#dbeafe", icon: <GroupIcon /> },
    { label: "Insured Employees", value: loading ? "…" : insuredCount, color: "#10b981", bg: "#d1fae5", icon: <ShieldIcon /> },
    { label: "Coverage Rate", value: loading ? "…" : `${coverageRate}%`, color: "#8b5cf6", bg: "#ede9fe", icon: <CheckCircleIcon /> },
    { label: "Active Policies", value: loading ? "…" : activePolicies.length, color: "#f59e0b", bg: "#fef3c7", icon: <LocalHospitalIcon /> },
  ];

  return (
    <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1f2937" }}>Insurance</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip icon={<VerifiedIcon sx={{ fontSize: 16 }} />} label="Powered by battleface"
            sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 600, fontSize: 13, px: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}>
            New policy
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map(({ label, value, color, bg, icon }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
              </Box>
              <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2937", lineHeight: 1.1, mt: 0.5 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Policies table */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>Policies</Typography>
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => company && loadPolicies(company)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
        <Divider sx={{ mb: 1 }} />
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={26} /></Box>
        ) : policies.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <ShieldIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No policies yet. Create your first policy to insure employees.</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)} sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}>New policy</Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Order", "Traveller(s)", "Trip", "Premium", "Status", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: "#6b7280" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((p) => {
                const st = STATUS_STYLES[p.status] || { bg: "#e5e7eb", color: "#374151", label: p.status || "—" };
                return (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{p.orderId || "—"}</TableCell>
                    <TableCell>{p.travellerNames || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#6b7280" }}>{p.trip?.startDate} → {p.trip?.endDate}<br />{p.trip?.destinationCountry}</TableCell>
                    <TableCell>{p.currency || ""} {p.premium ?? "—"}</TableCell>
                    <TableCell><Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600 }} /></TableCell>
                    <TableCell align="right"><IconButton size="small" onClick={(e) => openMenu(e, p)}><MoreVertIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem disabled={menuPolicy?.status === "cancelled"} onClick={() => { setModifyPolicy(menuPolicy); closeMenu(); }}>Modify policy</MenuItem>
        <MenuItem disabled={menuPolicy?.status === "cancelled"} onClick={() => { setCancelPolicy(menuPolicy); closeMenu(); }} sx={{ color: "#b91c1c" }}>Cancel policy</MenuItem>
      </Menu>

      {/* Coverage reference */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937", mb: 0.5 }}>What's Covered</Typography>
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 2.5 }}>Cover provided under product 1780 · base package.</Typography>
        <Divider sx={{ mb: 2.5 }} />
        <Grid container spacing={2}>
          {coverageItems.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: item.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 20 } })}
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1f2937" }}>{item.title}</Typography>
                    {item.deductible ? <Chip label={item.deductible} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "#f2f4f7", color: "#475467" }} /> : null}
                  </Box>
                  <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.6, fontSize: 13 }}>{item.description}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" sx={{ display: "block", mt: 2.5, color: "#98a2b3", lineHeight: 1.6 }}>{CONDITIONS_NOTE}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
          {POLICY_URL ? (<Button variant="outlined" size="small" href={POLICY_URL} target="_blank" sx={{ textTransform: "none", borderRadius: 2 }}>Policy wording</Button>) : null}
          <Button variant="outlined" size="small" href={DISCLOSURES_URL} target="_blank" sx={{ textTransform: "none", borderRadius: 2 }}>Insurance disclosures</Button>
        </Box>
        <Typography variant="caption" sx={{ display: "block", mt: 2, color: "#98a2b3", lineHeight: 1.55, fontSize: 11 }}>{LEGAL_FOOTER}</Typography>
      </Paper>

      <QuoteWizard open={wizardOpen} onClose={() => setWizardOpen(false)} employees={employees} company={company}
        onComplete={() => company && loadPolicies(company)} />
      <CancelDialog open={Boolean(cancelPolicy)} policy={cancelPolicy} onClose={() => setCancelPolicy(null)} onDone={() => company && loadPolicies(company)} />
      <ModifyDialog open={Boolean(modifyPolicy)} policy={modifyPolicy} onClose={() => setModifyPolicy(null)} onDone={() => company && loadPolicies(company)} />
    </Box>
  );
}
